import React, { useState } from "react";
import { api, authHeader, errMessage, downloadAuthed } from "../../api";

const BLANK = {
  medium: "",
  title: "",
  cadence: "Monthly-ish",
  neighbourhood: "",
  host: "",
  description: "",
  capacity: 6,
  seats_filled: 0,
  status: "forming",
  published: true,
};

function CircleForm({ initial, onCancel, onSaved }) {
  const editing = Boolean(initial?.id);
  const [f, setF] = useState(initial ? { ...BLANK, ...initial } : BLANK);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState("");

  const set = (k) => (e) => {
    const v = e.target.type === "checkbox" ? e.target.checked : e.target.value;
    setF((s) => ({ ...s, [k]: v }));
  };

  const save = async (e) => {
    e.preventDefault();
    setErr("");
    if (!f.medium.trim()) return setErr("It needs a medium \u2014 Writers, Painters, and so on.");
    const payload = {
      medium: f.medium.trim(),
      title: (f.title || "").trim(),
      cadence: (f.cadence || "").trim(),
      neighbourhood: (f.neighbourhood || "").trim(),
      host: (f.host || "").trim(),
      description: (f.description || "").trim(),
      capacity: Number(f.capacity) || 6,
      seats_filled: Number(f.seats_filled) || 0,
      status: f.status,
      published: Boolean(f.published),
    };
    setSaving(true);
    try {
      if (editing) {
        await api.put(`/admin/circles/${initial.id}`, payload, { headers: authHeader() });
      } else {
        await api.post("/admin/circles", payload, { headers: authHeader() });
      }
      onSaved();
    } catch (e2) {
      setErr(errMessage(e2, "That didn't save."));
    } finally {
      setSaving(false);
    }
  };

  return (
    <form className="adm-form" onSubmit={save} data-testid="admin-circle-form">
      <fieldset>
        <legend>{editing ? "Edit this circle" : "A new circle"}</legend>
        <div className="row3">
          <div className="field">
            <label htmlFor="c-medium">Medium</label>
            <input
              id="c-medium"
              value={f.medium}
              onChange={set("medium")}
              placeholder="Writers"
              data-testid="admin-circle-medium-input"
            />
          </div>
          <div className="field">
            <label htmlFor="c-cadence">How often</label>
            <input
              id="c-cadence"
              value={f.cadence}
              onChange={set("cadence")}
              data-testid="admin-circle-cadence-input"
            />
          </div>
          <div className="field">
            <label htmlFor="c-hood">Neighbourhood</label>
            <input
              id="c-hood"
              value={f.neighbourhood}
              onChange={set("neighbourhood")}
              placeholder="Bridgeland"
              data-testid="admin-circle-neighbourhood-input"
            />
          </div>
        </div>
        <div className="row3">
          <div className="field">
            <label htmlFor="c-cap">Seats in the circle</label>
            <input
              id="c-cap"
              type="number"
              min={1}
              value={f.capacity}
              onChange={set("capacity")}
              data-testid="admin-circle-capacity-input"
            />
            <span className="hint">Three to six is the shape that works.</span>
          </div>
          <div className="field">
            <label htmlFor="c-filled">Seats already taken</label>
            <input
              id="c-filled"
              type="number"
              min={0}
              value={f.seats_filled}
              onChange={set("seats_filled")}
              data-testid="admin-circle-filled-input"
            />
            <span className="hint">The public page shows what&rsquo;s left.</span>
          </div>
          <div className="field">
            <label htmlFor="c-status">Where it&rsquo;s at</label>
            <select
              id="c-status"
              value={f.status}
              onChange={set("status")}
              data-testid="admin-circle-status-select"
            >
              <option value="forming">Forming</option>
              <option value="open">Open</option>
              <option value="full">Full</option>
              <option value="paused">Paused</option>
            </select>
          </div>
        </div>
        <div className="field">
          <label htmlFor="c-desc">
            What it is <span className="opt">&mdash; optional</span>
          </label>
          <textarea
            id="c-desc"
            rows={3}
            value={f.description}
            onChange={set("description")}
            data-testid="admin-circle-description-textarea"
          />
        </div>
        <div className="check">
          <input
            id="c-pub"
            type="checkbox"
            checked={Boolean(f.published)}
            onChange={set("published")}
            data-testid="admin-circle-published-checkbox"
          />
          <div>
            <label htmlFor="c-pub">Show this on the public site.</label>
            <span className="hint">
              People can put their name down for it as soon as it&rsquo;s showing.
            </span>
          </div>
        </div>
      </fieldset>

      {err ? <div className="form-error">{err}</div> : null}

      <div className="actions">
        <button
          type="submit"
          className="send"
          disabled={saving}
          data-testid="admin-circle-save-button"
        >
          {saving ? "Saving\u2026" : editing ? "Save changes" : "Create it"}
        </button>
        <button type="button" className="linkish" onClick={onCancel}>
          Never mind
        </button>
      </div>
    </form>
  );
}

export default function CirclesPanel({ rows, interest, reload, loading }) {
  const [mode, setMode] = useState(null);

  const remove = async (c) => {
    if (
      !window.confirm(
        `Delete the ${c.medium} circle?\n\nThis also removes the names put down for it.`,
      )
    )
      return;
    try {
      await api.delete(`/admin/circles/${c.id}`, { headers: authHeader() });
      reload();
    } catch (e) {
      window.alert(errMessage(e));
    }
  };

  if (mode === "new" || mode?.edit) {
    return (
      <CircleForm
        initial={mode?.edit}
        onCancel={() => setMode(null)}
        onSaved={() => {
          setMode(null);
          reload();
        }}
      />
    );
  }

  return (
    <>
      <section className="admin-sec" data-testid="admin-circles-panel">
        <header>
          <h2>Circles</h2>
          <button
            type="button"
            className="act"
            onClick={() => setMode("new")}
            data-testid="admin-circles-create-button"
          >
            Add a circle &rarr;
          </button>
        </header>
        <p className="admin-help">
          Circles are the standing groups, as opposed to dated gatherings. People
          browse these by medium and put their name down; you decide when
          there&rsquo;s room.
        </p>

        {loading ? (
          <p className="admin-help">Looking&hellip;</p>
        ) : rows.length === 0 ? (
          <div className="empty" data-testid="admin-circles-empty">
            <h3 style={{ marginBottom: ".8rem" }}>No circles yet.</h3>
            <p>
              Add one per medium &mdash; Writers, Painters, Songwriters &mdash; and
              people can start putting their names down.
            </p>
          </div>
        ) : (
          <>
            <table className="tbl" data-testid="admin-circles-table">
              <thead>
                <tr>
                  <th>Medium</th>
                  <th>Where &amp; how often</th>
                  <th className="right">Seats</th>
                  <th className="right">Names down</th>
                  <th>State</th>
                  <th className="right">&nbsp;</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((c) => (
                  <tr key={c.id} data-testid={`admin-circle-row-${c.id}`}>
                    <td>
                      <span className="t">{c.title || `${c.medium} circle`}</span>
                    </td>
                    <td>
                      {[c.neighbourhood, c.cadence].filter(Boolean).join(" \u00b7 ") ||
                        "\u2014"}
                    </td>
                    <td className="num right">
                      {c.seats_filled} / {c.capacity}
                    </td>
                    <td className="num right">{c.interest_count}</td>
                    <td>
                      <span className={`chip ${c.published ? "chip--on" : "chip--off"}`}>
                        {c.published ? c.status : "hidden"}
                      </span>
                    </td>
                    <td className="right">
                      <div className="rowacts">
                        <button
                          type="button"
                          className="linkish"
                          onClick={() => setMode({ edit: c })}
                          data-testid={`admin-circle-edit-button-${c.id}`}
                        >
                          Edit
                        </button>
                        <button
                          type="button"
                          className="linkish"
                          onClick={() => remove(c)}
                          data-testid={`admin-circle-delete-button-${c.id}`}
                        >
                          Delete
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>

            <div className="stack">
              {rows.map((c) => (
                <div className="rowcard" key={c.id}>
                  <div className="rt">{c.title || `${c.medium} circle`}</div>
                  <span className={`chip ${c.published ? "chip--on" : "chip--off"}`}>
                    {c.published ? c.status : "hidden"}
                  </span>
                  <dl>
                    <dt>Where</dt>
                    <dd>{[c.neighbourhood, c.cadence].filter(Boolean).join(" \u00b7 ")}</dd>
                    <dt>Seats</dt>
                    <dd>
                      {c.seats_filled} / {c.capacity}
                    </dd>
                    <dt>Names</dt>
                    <dd>{c.interest_count}</dd>
                  </dl>
                  <div className="rowacts">
                    <button type="button" className="linkish" onClick={() => setMode({ edit: c })}>
                      Edit
                    </button>
                    <button type="button" className="linkish" onClick={() => remove(c)}>
                      Delete
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </>
        )}
      </section>

      <section className="admin-sec" data-testid="admin-interest-panel">
        <header>
          <h2>Names put down</h2>
          {interest.length ? (
            <button
              type="button"
              className="act"
              onClick={() =>
                downloadAuthed(
                  "/admin/export/circle-interest.csv",
                  "schema-circle-interest.csv",
                )
              }
              data-testid="admin-interest-export-button"
            >
              Export (CSV)
            </button>
          ) : null}
        </header>
        {interest.length === 0 ? (
          <div className="empty" data-testid="admin-interest-empty">
            <p>Nobody has put their name down yet.</p>
          </div>
        ) : (
          <>
            <table className="tbl" data-testid="admin-interest-table">
              <thead>
                <tr>
                  <th>Circle</th>
                  <th>Name</th>
                  <th>Email</th>
                  <th>What they make</th>
                </tr>
              </thead>
              <tbody>
                {interest.map((i) => (
                  <tr key={i.id}>
                    <td>{i.circle_medium}</td>
                    <td>
                      <span className="t">{i.name}</span>
                    </td>
                    <td>
                      <a href={`mailto:${i.email}`}>{i.email}</a>
                    </td>
                    <td>{i.note || "\u2014"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            <div className="stack">
              {interest.map((i) => (
                <div className="rowcard" key={i.id}>
                  <div className="rt">{i.name}</div>
                  <dl>
                    <dt>Circle</dt>
                    <dd>{i.circle_medium}</dd>
                    <dt>Email</dt>
                    <dd>
                      <a href={`mailto:${i.email}`}>{i.email}</a>
                    </dd>
                    {i.note ? (
                      <>
                        <dt>Makes</dt>
                        <dd>{i.note}</dd>
                      </>
                    ) : null}
                  </dl>
                </div>
              ))}
            </div>
          </>
        )}
      </section>
    </>
  );
}
